import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import MobileLayout from '../../components/MobileLayout';

/** The title-page contact block; mirrors the API's AuthorDetails (null = not set). */
interface AuthorDetailsData {
  legalName: string;
  penName: string;
  address: string;
  phone: string;
  contactEmail: string;
  representation: string;
  registration: string;
}

type Field = keyof AuthorDetailsData;

const EMPTY: AuthorDetailsData = {
  legalName: '',
  penName: '',
  address: '',
  phone: '',
  contactEmail: '',
  representation: '',
  registration: '',
};

const inputClass =
  'w-full px-4 py-3 rounded-xl border bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500';
const inputStyle = { borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' };

const AuthorDetails: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<AuthorDetailsData>(EMPTY);
  const [accountEmail, setAccountEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // The account email is only a placeholder hint, so its failure is not fatal.
    axios
      .get('/users/profile')
      .then((res) => !cancelled && setAccountEmail(res.data?.email || ''))
      .catch(() => undefined);
    axios
      .get('/users/author-details')
      .then((res) => {
        if (cancelled || !res.data) return;
        const next = { ...EMPTY };
        for (const key of Object.keys(EMPTY) as Field[]) next[key] = res.data[key] ?? '';
        setData(next);
      })
      .catch(() => {
        // Never show an empty form we could then save over real details.
        if (!cancelled) {
          setLoadFailed(true);
          toast.error(t('authorDetails.loadError', 'Could not load your author details'));
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [t]);

  const set = (key: Field) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setData((d) => ({ ...d, [key]: e.target.value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Empty strings clear a field; the email is trimmed so the API's check sees what is kept.
      await axios.put('/users/author-details', {
        ...data,
        contactEmail: data.contactEmail.trim(),
      });
      toast.success(t('authorDetails.saved', 'Author details saved'));
      navigate('/profile');
    } catch (err: any) {
      // Validation errors arrive as a list of messages; show the first.
      const message = err?.response?.data?.message;
      toast.error(
        (Array.isArray(message) ? message[0] : message) ||
          t('authorDetails.saveError', 'Failed to save author details'),
      );
    } finally {
      setSaving(false);
    }
  };

  const field = (
    key: Field,
    label: string,
    opts: {
      maxLength: number;
      multiline?: boolean;
      type?: string;
      placeholder?: string;
      hint?: string;
    },
  ) => {
    const id = `author-${key}`;
    return (
      <div>
        <label
          htmlFor={id}
          className="block text-sm font-medium mb-2"
          style={{ color: 'var(--ds-text-primary)' }}
        >
          {label}
        </label>
        {opts.multiline ? (
          <textarea
            id={id}
            value={data[key]}
            onChange={set(key)}
            rows={3}
            maxLength={opts.maxLength}
            placeholder={opts.placeholder}
            className={`${inputClass} resize-y`}
            style={inputStyle}
          />
        ) : (
          <input
            id={id}
            type={opts.type ?? 'text'}
            value={data[key]}
            onChange={set(key)}
            maxLength={opts.maxLength}
            placeholder={opts.placeholder}
            className={inputClass}
            style={inputStyle}
          />
        )}
        {opts.hint && (
          <p className="text-xs mt-1" style={{ color: 'var(--ds-text-secondary)' }}>
            {opts.hint}
          </p>
        )}
      </div>
    );
  };

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--ds-border)' }}>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              aria-label={t('common.back', 'Back')}
              className="w-10 h-10 flex items-center justify-center rounded-full"
            >
              <ArrowLeftIcon className="w-5 h-5" style={{ color: 'var(--ds-text-primary)' }} />
            </button>
            <h1 className="ml-4 text-xl font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
              {t('authorDetails.title', 'Author details')}
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div
              className="animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
            />
          </div>
        ) : loadFailed ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <p className="mb-4" style={{ color: 'var(--ds-text-secondary)' }}>
              {t('authorDetails.loadError', 'Could not load your author details')}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold"
            >
              {t('common.retry', 'Retry')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSave} className="flex-1 px-6 py-6 space-y-6">
            <p className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
              {t(
                'authorDetails.intro',
                'These appear on the title pages of the scripts and manuscripts you export. They are never sent to the AI. Every field is optional.',
              )}
            </p>
            {field('legalName', t('authorDetails.legalName', 'Legal name'), { maxLength: 200 })}
            {field('penName', t('authorDetails.penName', 'Pen name / byline'), {
              maxLength: 200,
              hint: t('authorDetails.penNameHint', 'Leave blank to use your legal name.'),
            })}
            {field('address', t('authorDetails.address', 'Address'), {
              maxLength: 1000,
              multiline: true,
            })}
            {field('phone', t('authorDetails.phone', 'Phone'), { maxLength: 200, type: 'tel' })}
            {field('contactEmail', t('authorDetails.contactEmail', 'Contact email'), {
              maxLength: 200,
              type: 'email',
              placeholder: accountEmail || undefined,
              hint: t('authorDetails.contactEmailHint', 'Leave blank to use your account email.'),
            })}
            {field('representation', t('authorDetails.representation', 'Representation'), {
              maxLength: 1000,
              multiline: true,
              hint: t(
                'authorDetails.representationHint',
                'Your agent or manager, one detail per line.',
              ),
            })}
            {field('registration', t('authorDetails.registration', 'Registration'), {
              maxLength: 200,
              placeholder: t('authorDetails.registrationPlaceholder', 'WGA Registered #…'),
              hint: t('authorDetails.registrationHint', 'Shown on screenplay title pages only.'),
            })}
            <button
              type="submit"
              disabled={saving}
              className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-xl transition-colors"
            >
              {saving ? t('authorDetails.saving', 'Saving…') : t('authorDetails.save', 'Save')}
            </button>
          </form>
        )}
      </div>
    </MobileLayout>
  );
};

export default AuthorDetails;
